import { MonetizationType, ObjectType, PresentationType } from '@/@types/graphql-types';

import { CollectionType } from '@/enums/collection-type';
import { ProviderType } from '@/components/filter-bar/filter-bar.types';
import { TabType } from '@/interfaces/inner-tabs';
import { TitleListLayout } from '@/enums/title-list-layout';
import { TitleOrder } from '@/enums/title-order';

export interface FilterCollection {
	ageCertifications: string[]; // "technicalName" of AgeCertification
	objectTypes: ObjectType[];
	providers_type: ProviderType | null;
	exclude_providers: string[];
	genres: string[]; // "short_code" from different genres
	excludeGenres: string[];
	event_date: Date | undefined;
	languages: string[] | null;
	min_price: number | null;
	min_runtime: number | null; // minutes
	matching_offers_only: boolean | null;
	max_price: number | null;
	max_runtime: number | null; // minutes
	monetizationTypes: MonetizationType[];
	presentationTypes: PresentationType[];
	providers: string[]; // "shortName"s of different providers
	release_year_from: number | null;
	release_year_until: number | null;
	scoring_filter_types: Record<string, { min_scoring_value: number; max_scoring_value?: number }> | null;
	timeline_type: string | null;
	sort_by: TitleOrder | null;
	sort_asc: boolean | null;
	inner_tab?: TabType;
	list_layout?: TitleListLayout;
	productionCountries: string[];
	excludeProductionCountries: string[];
	is_upcoming: boolean;
	sorting_random_seed?: number;
	// SUBGENRES
	subgenres?: string[];
}

export type FilterName = keyof FilterCollection;

export interface FilterValue {
	collectionType: CollectionType;
	filterName: FilterName;
	filterValue: any;
}

export type CountryFilters = {
	[collection in CollectionType]: FilterCollection;
};
export type FilterState = {
	[country: string]: CountryFilters;
};

export const getDefaultFilterCollection = (): FilterCollection => ({
	ageCertifications: [], // "technicalName" of AgeCertification
	objectTypes: [],
	providers_type: null,
	exclude_providers: [],
	genres: [], // "short_code" from different genres
	excludeGenres: [],
	event_date: undefined,
	languages: null,
	min_price: null,
	min_runtime: null,
	matching_offers_only: null,
	max_price: null,
	max_runtime: null,
	monetizationTypes: [],
	presentationTypes: [],
	providers: [], // "shortName"s of different providers
	release_year_from: null,
	release_year_until: null,
	scoring_filter_types: null,
	timeline_type: null,
	sort_by: null,
	sort_asc: null,
	productionCountries: [],
	excludeProductionCountries: [],
	is_upcoming: false,
	subgenres: [],
});

export const platformCollectionTypes: CollectionType[] = Object.values(CollectionType);

/**
 * this interface is used for URL param mapping from URL to filter states.
 */
export type CollectionStateUrlParams = Dictionary<string>;
/**
 * since the url query parameters can differ from the base collection state one, we have this monster of a type here.
 */
export type CollectionStateUrlQuery = Partial<
	Record<
		Exclude<
			| keyof FilterCollection
			| 'q'
			| 'list_id'
			| 'person_id'
			| 'preferred_variant'
			| 'remove_preferred_experiment'
			| 'new_preferred_variant'
			| 'new_remove_preferred_experiment'
			| 'snowplow'
			| 'content_type'
			| 'age_certifications'
			| 'exclude_genres'
			| 'production_countries'
			| 'exclude_production_countries'
			| 'monetization_types'
			| 'presentation_types',
			| 'objectTypes'
			| 'ageCertifications'
			| 'excludeGenres'
			| 'productionCountries'
			| 'excludeProductionCountries'
			| 'monetizationTypes'
			| 'presentationTypes'
		>,
		string
	>
>;
